"use client";

import { useState } from "react";
import { Check, ChevronRight } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { useOrderTotals } from "@/hooks/use-order-totals";
import { useFees } from "@/context/fees-context";
import { useAuth } from "@/context/auth-context";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useCaliiPlusMembership } from "@/hooks/use-calii-plus-membership";
import CaliiPlusModalContent from "./calii-plus-modal-content";

// TODO: consolidate with CALII_PLUS_TIERS in lib/calii-plus.ts once fetched from API.
const CALII_PLUS_PROGRESS_TIERS = [
  { threshold: 300,  label: "Envío gratis" },
  { threshold: 800,  label: "$50 OFF" },
  { threshold: 1200, label: "$80 OFF" },
  { threshold: 1500, label: "$100 OFF" },
  { threshold: 2250, label: "$150 OFF" },
  { threshold: 3000, label: "$200 OFF" },
  { threshold: 4000, label: "$250 OFF" },
] as const;

function MilestoneCheckbox({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-1 text-xs">
      <span className="text-muted-foreground">✓ {label}</span>
    </div>
  );
}

function CaliiPlusMemberProgress({ subtotal, className }: { subtotal: number; className?: string }) {
  const tiers = CALII_PLUS_PROGRESS_TIERS;
  const targetIndex = tiers.findIndex((t) => subtotal < t.threshold);
  const isMaxReached = targetIndex === -1;

  const targetTier = isMaxReached ? tiers[tiers.length - 1] : tiers[targetIndex];

  const progress = isMaxReached
    ? 100
    : Math.min((subtotal / targetTier.threshold) * 100, 100);

  const remaining = isMaxReached ? 0 : Math.ceil(targetTier.threshold - subtotal);

  // First milestone checkbox: "Envío gratis", shown once subtotal >= 300
  const showFirstMilestone = subtotal >= 300;

  // Second milestone checkbox: the most recently unlocked discount tier (tiers[targetIndex - 1]).
  // Shown when subtotal >= 800 (i.e. at least one discount tier has been reached).
  // Always checked when shown (subtotal already exceeds its threshold by definition).
  const effectiveTargetIndex = isMaxReached ? tiers.length : targetIndex;
  const secondMilestoneTier = effectiveTargetIndex >= 2 ? tiers[effectiveTargetIndex - 1] : null;

  return (
    <div className={className}>
      <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
        <span>$0</span>
        <span>${targetTier.threshold}</span>
      </div>
      <Progress value={progress} className="h-1.5 [&>[data-slot=progress-indicator]]:bg-teal-400" />
      <p className="mt-3 text-center text-xs text-muted-foreground">
        {isMaxReached ? (
          <span></span>
        ) : (
          <span>
            <span>${remaining}</span> para <span className="font-medium text-foreground">{targetTier.label.toLowerCase()}</span>
          </span>
        )}
      </p>
      {(showFirstMilestone || secondMilestoneTier) && (
        <div className="flex gap-3 mt-2 justify-center">
          {showFirstMilestone && <MilestoneCheckbox label="Envío gratis" />}
          {secondMilestoneTier && <MilestoneCheckbox label={secondMilestoneTier.label} />}
        </div>
      )}
    </div>
  );
}

export default function FreeDeliveryProgress({ className }: { className?: string }) {
  const rawFees = useFees();
  const { subtotal } = useOrderTotals();
  const { user } = useAuth();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const { freeTrialMonths } = useCaliiPlusMembership();
  const [caliiPlusOpen, setCaliiPlusOpen] = useState(false);

  const hasCaliiPlusCoupons = (user?.num_remaining_calii_plus_coupons ?? 1) > 0;
  if (user?.is_calii_plus_member && hasCaliiPlusCoupons) {
    return <CaliiPlusMemberProgress subtotal={subtotal} className={className} />;
  }

  const freeDeliveryMin = rawFees?.DEFAULT_FREE_DELIVERY_MIN ?? null;
  if (!freeDeliveryMin) return null;

  const progress = Math.min((subtotal / freeDeliveryMin) * 100, 100);

  return (
    <>
      <div className={className}>
        <div className="flex items-center justify-between text-[10px] text-muted-foreground mb-1">
          <span>$0</span>
          <span>${freeDeliveryMin}</span>
        </div>
        <Progress value={progress} className="h-1.5 [&>[data-slot=progress-indicator]]:bg-teal-400" />
        <p className="mt-1.5 text-center text-xs text-muted-foreground">
          {progress >= 100 ? (
            <span className="font-medium text-primary">✓ Envío gratis</span>
          ) : (
            <span>
              <span className="font-medium text-foreground">${Math.ceil(freeDeliveryMin - subtotal)}</span> para envío gratis
            </span>
          )}
        </p>
        {progress < 100 && !user?.is_calii_plus_member && (
          <button
            type="button"
            onClick={() => setCaliiPlusOpen(true)}
            className="w-full cursor-pointer flex items-center justify-center gap-0.5 mt-2 text-xs text-teal-600 hover:text-teal-700 transition-colors"
          >
            Obtén envío gratis con Calii+
            <ChevronRight className="h-3.5 w-3.5 shrink-0" />
          </button>
        )}
      </div>

      {isDesktop ? (
        <Dialog open={caliiPlusOpen} onOpenChange={setCaliiPlusOpen}>
          <DialogContent className="sm:max-w-md p-0 overflow-hidden gap-0 [&>button:last-child]:hidden flex flex-col max-h-[90svh]">
            <DialogTitle className="sr-only">Calii+</DialogTitle>
            <DialogDescription className="sr-only">Membresía con beneficios exclusivos</DialogDescription>
            <CaliiPlusModalContent onClose={() => setCaliiPlusOpen(false)} freeTrialMonths={freeTrialMonths} signupSource="free_delivery_progress_web" />
          </DialogContent>
        </Dialog>
      ) : (
        <Drawer open={caliiPlusOpen} onOpenChange={setCaliiPlusOpen}>
          <DrawerContent className="p-0 min-h-[75svh]">
            <DrawerTitle className="sr-only">Calii+</DrawerTitle>
            <DrawerDescription className="sr-only">Membresía con beneficios exclusivos</DrawerDescription>
            <CaliiPlusModalContent onClose={() => setCaliiPlusOpen(false)} freeTrialMonths={freeTrialMonths} signupSource="free_delivery_progress_web" />
          </DrawerContent>
        </Drawer>
      )}
    </>
  );
}
